'use client';
import React from 'react';

import styled, { TC } from '@benzinga/themetron';
import { soundAlerts } from '@benzinga/session';

import { Modal } from '@benzinga/core-ui';
import { Watchlist } from '@benzinga/watchlist-manager';
import { SessionContext } from '@benzinga/session-context';

import { ModalContent, ModalOption, ModalOptionText, ModalSubtitle, ModalTitle } from '@benzinga/core-ui';
import { Tooltip, Switch } from 'antd';
import { PlaySoundManager } from '@benzinga/play-sound-manager';
import { WidgetContext } from '@benzinga/widget-tools';

interface Props {
  closeModal: () => void;
  updateWatchlist: (watchlist: Watchlist) => void;
  watchlist: Watchlist;
}

interface State {
  watchlist: Watchlist;
}

export const WatchlistAlerts: React.FC<Props> = props => {
  const session = React.useContext(SessionContext);
  const widget = React.useContext(WidgetContext);
  const [state, setState] = React.useState<State>(() => ({
    watchlist: props.watchlist,
  }));

  React.useEffect(() => {
    const sync = setTimeout(
      () =>
        setState(s => ({
          ...s,
          watchlist: props.watchlist,
        })),
      1000,
    );

    return () => {
      clearTimeout(sync);
    };
  }, [props.watchlist]);

  const updateWatchlist = React.useCallback(
    (updateFields: Partial<Watchlist>) => {
      const updateWatchlist = props.updateWatchlist;
      setState(s => ({
        ...s,
        watchlist: { ...s.watchlist, ...updateFields },
      }));
      updateWatchlist({ ...state.watchlist, ...updateFields });
    },
    [props.updateWatchlist, state.watchlist],
  );

  const updateRealtimeBZ = React.useCallback(
    (realtimeBZ: boolean) => {
      updateWatchlist({ realtimeBZ });
    },
    [updateWatchlist],
  );
  const updateRealtimePR = React.useCallback(
    (realtimePR: boolean) => {
      updateWatchlist({ realtimePR });
    },
    [updateWatchlist],
  );
  const updateRealtimeSEC = React.useCallback(
    (realtimeSEC: boolean) => {
      updateWatchlist({ realtimeSEC });
    },
    [updateWatchlist],
  );
  const updateEmailSummary = React.useCallback(
    (emailSummary: boolean) => {
      updateWatchlist({ emailSummary });
    },
    [updateWatchlist],
  );

  const { closeModal } = props;

  const {
    watchlist: { emailSummary, name, realtimeBZ, realtimePR, realtimeSEC, sound },
  } = state;

  const dropdownMenuButton = soundAlerts.find(s => s.id === sound)?.name ?? 'None';

  const dropdownMenuItems = soundAlerts.map<TC.DropdownMenuItem>(({ id, name: value }) => ({
    id,
    onClick: () => {
      updateWatchlist({ sound: id });
      session
        .getManager(PlaySoundManager)
        .playSound(id, 'watchlist', `watchlist widget ${widget.widgetId} sound alert change test sound`);
    },
    value,
  }));

  return (
    <Modal
      onClose={closeModal}
      size={{ alert: true }}
      title={
        <ModalTitleSpan>
          <span className="secondary">Configure Alerts for: </span>
          <Tooltip className="u-primaryText" mouseEnterDelay={0.5} placement={'bottom'} title={name}>
            {name}
          </Tooltip>
        </ModalTitleSpan>
      }
    >
      <AlertContent>
        <ModalTitle>Sound Alerts</ModalTitle>
        <ModalSubtitle>Hear a sound the moment a news item related to this watchlist hits your newsfeed.</ModalSubtitle>
        <div className="Modal-dropdown">
          <WatchlistDropDown
            button={{ type: 'toolbar', value: dropdownMenuButton }}
            items={dropdownMenuItems}
            sort={true}
          />
        </div>

        <ModalTitle>Realtime Email Alerts</ModalTitle>
        <ModalSubtitle>
          Receive realtime email alerts for stories relevant to all symbols in this watchlist.
        </ModalSubtitle>

        <ModalOption>
          <Switch checked={realtimeBZ} onChange={updateRealtimeBZ} />
          <ModalOptionText>Benzinga Wire</ModalOptionText>
        </ModalOption>

        <ModalOption>
          <Switch checked={realtimePR} onChange={updateRealtimePR} />
          <ModalOptionText>Press Releases</ModalOptionText>
        </ModalOption>

        <ModalOption style={{ marginBottom: 0 }}>
          <Switch checked={realtimeSEC} onChange={updateRealtimeSEC} />
          <ModalOptionText>SEC</ModalOptionText>
        </ModalOption>

        <ModalTitle>Email Summaries</ModalTitle>
        <ModalSubtitle>
          Separate from realtime alerts, a daily digest of the day&apos;s happenings in one email. Sent after 7am and
          5:30pm (UTC-05:00, Eastern Time) every trading day.
        </ModalSubtitle>
        <ModalOption>
          <Switch checked={Boolean(emailSummary)} onChange={updateEmailSummary} />
        </ModalOption>
      </AlertContent>
    </Modal>
  );
};

const WatchlistDropDown = styled(TC.DropdownMenu)`
  line-height: 2em !important;
  margin-bottom: 0 !important;
  max-width: 250px;
  min-width: 100px;
  border: 1px solid ${props => props.theme.colors.backgroundInactive};
`;

const AlertContent = styled(ModalContent)`
  overflow-y: scroll;
`;

const ModalTitleSpan = styled.span`
  max-width: 100%;
  vertical-align: bottom;
  .secondary {
    padding-right: 2px;
  }
  .u-primaryText {
    display: inline-block;
    max-width: 100%;
    color: ${props => props.theme.colors.foreground};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    vertical-align: inherit;
  }
`;
